import React, { useState, useEffect } from 'react';

import { Box, Dialog, DialogContent, CircularProgress } from '@mui/material';

import Eideasy from '../../eideasy/eideasy';
import { useSelector } from '../../../redux/store';
import { TimeStamps, TimeStampPages } from './DocumentSigning';
import documentService from '../../../services/documentService';
import { SignatureOption } from '../../../@types/tenants/types';

//----------------------------------------------------------------------

type DocumentEIDEasySigningDialogProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  timestampPosition: TimeStamps;
  timestampPage: TimeStampPages;
};

const DocumentEIDEasySigningDialog: React.FC<DocumentEIDEasySigningDialogProps> = ({
  isOpen,
  setIsOpen,
  timestampPosition,
  timestampPage,
}) => {
  const { selectedId } = useSelector((state) => state.document);
  const [prepareForSigning, setPrepareForSigning] = useState({
    documentId: '',
    clientId: '',
  });
  const [enabledCountries, setEnabledCountries] = useState<string[]>([]);
  const [enabledMethods, setEnabledMethods] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleClose = () => {
    setPrepareForSigning({
      documentId: '',
      clientId: '',
    });
    setIsOpen(false);
  };

  const getDocId = async (selectedId: number) => {
    try {
      const prepareForSigningDocument = await documentService.getEidEasyDocId(
        selectedId,
        timestampPosition,
        timestampPage
      );
      const eidEasySettings = await documentService.getEidEasyMethods();

      const countries = Object.keys(eidEasySettings);
      const signatureOptions: SignatureOption[][] = Object.values(eidEasySettings);

      const methods: string[] = [];

      signatureOptions.forEach((m: SignatureOption[]) => {
        m.forEach((s: SignatureOption) => {
          methods.push(s.actionType);
        });
      });

      setEnabledCountries(countries);
      setEnabledMethods([...new Set(methods)]);
      setPrepareForSigning(prepareForSigningDocument);
    } catch (e) {
      setPrepareForSigning({
        documentId: '',
        clientId: '',
      });
    }
  };

  useEffect(() => {
    if (isOpen && selectedId) {
      getDocId(selectedId);
    }
  }, [isOpen]);

  useEffect(() => {
    if (prepareForSigning.documentId.length > 0 && prepareForSigning.clientId.length > 0) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [prepareForSigning]);

  useEffect(() => {
    if (isOpen) {
      const sel = 'div[class^="AppFooter_footer_"]';

      const patch = () => {
        const el = document.querySelector<HTMLDivElement>(sel);
        if (!el) return false;

        el.innerHTML = `<div style="width: 100%; text-align: center">DocLogix, UAB</div>`;
        return true;
      };

      if (patch()) return;

      const mo = new MutationObserver(() => {
        if (patch()) mo.disconnect();
      });

      mo.observe(document.body, { childList: true, subtree: true });
      return () => mo.disconnect();
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} fullWidth maxWidth="md" onClose={handleClose}>
      <DialogContent sx={{ maxHeight: '80vh', minHeight: '240px' }}>
        {!isLoading && selectedId && (
          <Eideasy
            eidEasyDocumentId={prepareForSigning.documentId}
            clientId={prepareForSigning.clientId}
            enabledCountries={enabledCountries}
            enabledMethods={enabledMethods}
            documentId={selectedId}
            setIsLoading={setIsLoading}
            setIsOpen={setIsOpen}
          />
        )}

        {isLoading && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              minHeight: '240px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={80} sx={{ margin: 'auto' }} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentEIDEasySigningDialog;
